// src/encounter/lib/to-previous-notes.ts
// Pre-encounter PREVIOUS NOTES mapper (PROD-1523).
//
// Port of Flutter `previousNotes()` (pre_encounter_widget.dart:893) + the per-row
// `NotesRowWidget` (pre_encounter_widget.dart:2050). For a follow-up encounter the section lists
// each PRIOR COMPLETED encounter as a row: a clinical icon + reason-for-visit title (blue) with a
// diagnosis summary appended (grey), a "{locationName}  •  {dateTime}" meta line, and a "View"
// action that opens the note. Flutter behaviour reproduced here:
//   • FILTER to prior notes only — drop the CURRENT encounter (identifier !== current) and keep
//     only status "Completed" (AppConstants.encounterStatusCompleted; matched case-insensitively).
//   • diagnosis summary = the matching PatientAssessment's assessmentPlanCondition[].description
//     values concatenated (Flutter joins with a leading space per item); we trim to a clean string.
//   • the whole section is HIDDEN when there are no qualifying notes (Flutter returns Container()).
//
// PURE — no I/O. The parent wires the real data (previousEncounterNotes + assessmentResponse +
// appointment.reasonCode.display); this maps a reasonable raw shape to the view model.

/** Status string Flutter treats as a completed encounter (AppConstants.encounterStatusCompleted = "Completed", what
 *  Scrips.Patient WrapUpService writes on sign). Compared case-insensitively. */
export const ENCOUNTER_STATUS_COMPLETED = 'Completed';

/** Minimal shape of a prior encounter (Flutter EncounterModel, only the fields this row reads). */
export interface RawPreviousEncounter {
  /** Encounter id (Flutter `identifier`) — used to drop the current encounter + match diagnoses. */
  identifier?: string | null;
  /** Encounter status; only "completed" rows are shown. */
  status?: string | null;
  /** Facility/location name for the meta line (Flutter `locationName`). */
  locationName?: string | null;
  /** Encounter start date-time, ISO string (Flutter `startDate`). */
  startDate?: string | null;
}

/** Minimal shape of a prior encounter's assessment (Flutter PatientAssessment). */
export interface RawPreviousAssessment {
  /** The encounter id this assessment belongs to (Flutter PatientAssessment.encounter). */
  encounter?: string | null;
  /** The diagnoses on that encounter; we concatenate their descriptions. */
  assessmentPlanCondition?: Array<{ description?: string | null }> | null;
}

/** Raw inputs the parent hands the mapper (mirrors Flutter's `previousEncounterNotes` + appt RFV). */
export interface PreviousNotesInput {
  /** All prior encounter notes (Flutter previousEncounterNotes.enconterDetails). */
  encounters?: RawPreviousEncounter[] | null;
  /** All prior assessments, keyed to their encounter (Flutter previousEncounterNotes.assessmentResponse). */
  assessments?: RawPreviousAssessment[] | null;
  /** Reason-for-visit title shown on every row (Flutter appointment.reasonCode.display). */
  reasonForVisit?: string | null;
  /** The current encounter id to EXCLUDE from the list (Flutter bloc.encounterId()). */
  currentEncounterId?: string | null;
}

export interface PreviousNoteView {
  /** Stable React key + handle for opening the note (the prior encounter's identifier). */
  id: string;
  /** Reason-for-visit title (blue), shown per row (Flutter reasonForVisit). */
  reasonForVisit: string;
  /** Diagnoses summary appended after the title (grey) — undefined when the note has none. */
  diagnosisSummary?: string;
  /** Facility/location name for the meta line — undefined when unknown. */
  locationLabel?: string;
  /** Formatted start date-time for the meta line — undefined when unknown/unparseable. */
  dateTimeLabel?: string;
  /** The encounter's start as the API sent it (ISO), for a surface that formats the date itself (the pre-visit's
   *  Previous notes card, previsit-history/). Undefined when unknown. */
  startDate?: string;
}

/** Join a condition list's descriptions into one grey summary string (Flutter appends each). */
function diagnosisSummaryOf(assessment: RawPreviousAssessment | undefined): string | undefined {
  const parts = (assessment?.assessmentPlanCondition ?? [])
    .map((c) => c?.description?.trim())
    .filter((d): d is string => !!d && d.length > 0);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

/** ISO → short readable date-time; returns the raw string when unparseable, undefined when absent. */
function formatDateTime(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

/**
 * Map the raw pre-encounter inputs to Previous-Notes view models.
 * Flutter parity: keep only prior COMPLETED encounters (exclude the current one), one row each.
 */
export function toPreviousNotes(input: PreviousNotesInput | null | undefined): PreviousNoteView[] {
  const encounters = input?.encounters;
  if (!encounters || encounters.length === 0) return [];

  const currentId = input?.currentEncounterId ?? null;
  const reasonForVisit = input?.reasonForVisit?.trim() ?? '';

  // Index assessments by their encounter id for the per-row diagnosis lookup (Flutter firstWhere).
  const assessmentByEncounter = new Map<string, RawPreviousAssessment>();
  for (const a of input?.assessments ?? []) {
    if (a?.encounter) assessmentByEncounter.set(a.encounter, a);
  }

  const out: PreviousNoteView[] = [];
  for (const enc of encounters) {
    const id = enc?.identifier ?? undefined;
    // Flutter filter: drop the current encounter + keep only completed notes.
    if (!id || id === currentId) continue;
    if ((enc?.status ?? '').trim().toLowerCase() !== ENCOUNTER_STATUS_COMPLETED.toLowerCase()) continue;

    out.push({
      id,
      reasonForVisit,
      diagnosisSummary: diagnosisSummaryOf(assessmentByEncounter.get(id)),
      locationLabel: enc?.locationName?.trim() || undefined,
      dateTimeLabel: formatDateTime(enc?.startDate),
      startDate: enc?.startDate || undefined,
    });
  }

  return out;
}
